'use client';

import { FC } from 'react';

import { Breadcrumbs, BreadcrumbsProps } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { getHomeUrl } from '@telialt/core-navigation/src/utils/path';
import { isSSR } from '@telialt/utils/src/helpers';
import { usePathname } from '@telialt/utils/src/pathname';

import { useLandingPageBreadcrumbs } from './hooks/useLandingPageBreadcrumbs';
import { BreadcrumbsConfig } from './model';

type Inherited = Omit<BreadcrumbsProps, 'children' | 'meta' | 'baseUrl' | 'aria-label'>;
type Props = Partial<BreadcrumbsConfig> & Inherited;

/**
 * @component
 * Breadcrumbs for landing pages, meaning includes hardcoded Home ("Pagrindinis") breadcrumb by default.
 */
export const LandingPageBreadcrumbs: FC<Props> = ({
    currentPath: forcedCurrentPath,
    knownBreadcrumbs,
    ...inheritedProps
}) => {
    const { t } = useTranslations();
    const pathname = usePathname();
    const currentPath = forcedCurrentPath ?? pathname;
    const homeUrl = getHomeUrl(currentPath);
    const breadcrumbs = useLandingPageBreadcrumbs({ currentPath, knownBreadcrumbs });

    if (!breadcrumbs?.length) {
        return null;
    }

    const metaBaseUrl =
        process.env.HYBRIS_PUBLIC_URL || (!isSSR() && window.location.hostname) || 'https://www.telia.lt';

    return (
        <Breadcrumbs
            {...inheritedProps}
            aria-label={t['common.breadcrumb_navigation.aria_label']}
            baseUrl={metaBaseUrl}
            meta={true}
            data-section="Breadcrumbs"
        >
            {[
                <Breadcrumbs.Item
                    key="constant"
                    href={homeUrl}
                    data-element="Link"
                    data-element-type="Breadcrumb"
                    data-element-name={t['common.breadcrumb_navigation.home']}
                >
                    {t['common.breadcrumb_navigation.home']}
                </Breadcrumbs.Item>,
                ...breadcrumbs.map((breadcrumb, index) => (
                    <Breadcrumbs.Item
                        key={index}
                        href={breadcrumb.url}
                        data-element="Link"
                        data-element-type="Breadcrumb"
                        data-element-name={breadcrumb.title}
                    >
                        {breadcrumb.title}
                    </Breadcrumbs.Item>
                )),
            ]}
        </Breadcrumbs>
    );
};
